import { OrderType } from "@biz/Definitions";
import { LoungeType } from "@biz/definitions/LoungeDefinitions";
import { Product } from "@biz/definitions/Product";
import { SST } from "@biz/definitions/SST";
import { SunSailType } from "@biz/definitions/SunSailDefinitions";
import { IProductDescriptorIntersection, ISunSailExtendedType } from "@biz/ISunSailExtendedType";
import { CurtainType } from "@biz/items/curtain/CurtainDefinitions";
import { RaffType } from "@biz/items/raff/RaffDefinitions";
import { Poles } from "@biz/items/sunSail/logic/Poles";
import { IProduct2dForm } from "@biz/models/Sail2dDefinitions";
import { PD } from "@biz/PD";
import { IExtendedProductDescriptor, IProductDescriptor } from "@biz/ProductDescriptor";
import { ProductData } from "@biz/projectCalculation/ProjectDefinitions";

/** @deprecated */
export function getSunSailExtendedFromProduct(product: ProductData): ISunSailExtendedType {
  const orderType = Product.getOrderType(product);
  const allInOne = orderType === "HS";
  const count = product.count;
  if (Product.isSunSail(product)) {
    return {
      version: product.version,
      allInOne,
      orderType,
      count,
      sunSailType: product.sunSailType,
      raffType: RaffType.None, // no raff supported so far..
      poleCount: Poles.getCount(product),
    }
  } else if (Product.isModelProduct(product)) {
    return {
      version: product.version,
      allInOne,
      orderType,
      count,
      sunSailType: SunSailType.ModelProduct,
      raffType: RaffType.None,
      templateId: product.templateId,
      poleCount: 0,
    }
  } else if (Product.isLounge(product)) {
    const orderType: OrderType = "H" as OrderType; // sail.orderType
    return {
      version: product.version,
      allInOne,
      orderType,
      count,
      sunSailType: SunSailType.Lounge,
      raffType: RaffType.None,
      loungeType: product.loungeType,
      poleCount: 0,
    }
  } else if (Product.isCurtain(product) || Product.isCurtain2d(product)) {
    return {
      version: product.version,
      allInOne,
      orderType,
      count,
      sunSailType: SunSailType.Curtain,
      raffType: RaffType.None,
      curtainType: product.curtainType,
      poleCount: 0,
    }
  } else if (Product.isRaff(product)) {
    return {
      version: product.version,
      allInOne,
      orderType,
      count,
      sunSailType: PD.productToSunSailType(product),
      raffType: product.raffType,
      poleCount: 0,
    };
  } else if (Product.isNet(product)) {
    return {
      version: product.version,
      allInOne,
      orderType,
      count,
      sunSailType: PD.productToSunSailType(product),
    };
  } else if (Product.isStandardCollection2d(product)) {
    return {
      version: product.version,
      allInOne,
      orderType,
      count,
      sunSailType: SunSailType.StandardCollection,
      raffType: RaffType.None,
      poleCount: 0,
    };
  } else if (Product.isProduct2d(product)) {
    throw new Error("Product2d Not handled so far!");
  } else if (Product.isProductSC(product)) {
    throw new Error("ProductSC Not handled so far!");
  } else {
    throw new Error("Product Not handled so far!");
  }
}

/** @deprecated */
export function getSunSailExtendedFromProduct2dForm(product: IProduct2dForm): ISunSailExtendedType {
  let sunSailType: SunSailType;
  let poleCount: number | undefined;
  let curtainType: CurtainType | undefined = undefined;
  let raffType: RaffType | undefined = undefined;
  let loungeType: LoungeType | undefined = undefined;

  const pd = PD.productToPD(product, true);
  if (PD.isSail(pd)) {
    sunSailType = pd.sunSailType;
    poleCount = pd.poleCount;
  } else if (PD.isRaff(pd)) {
    sunSailType = PD.toSunSailType(pd)
    raffType = pd.raffType;
  } else if (PD.isCurtain(pd)) {
    sunSailType = SunSailType.Curtain;
    curtainType = pd.curtainType;
  } else if (PD.isLounge(pd)) {
    sunSailType = SunSailType.Lounge;
    loungeType = pd.loungeType;
  } else if (PD.isStandardCollection(pd)) {
    sunSailType = SunSailType.StandardCollection;
  } else {
    throw new Error("Not supported so far!");
  }
  return {
    sunSailType,
    poleCount,
    raffType,
    curtainType,
    loungeType,
    allInOne: false,
    orderType: "S",
    count: product.count,
    version: product.version
  };
}

/** @deprecated */
export function ssteToPDE(sunSailTypes: ISunSailExtendedType[]): IExtendedProductDescriptor[] {
  if (!sunSailTypes)
    return [];
  try {
    return sunSailTypes.map((sst) => {
      return {
        pd: sstToPD(sst),
        count: sst.count,
        orderType: sst.orderType ? sst.orderType : (sst.allInOne ? "HS" : "H")
      }
    });
  } catch (e) {
    console.error("Failed ssteToPDE", e);
  }
  return []
}

/** @deprecated */
export function sstToPD(pdi: IProductDescriptorIntersection): IProductDescriptor {
  if (pdi.sunSailType !== undefined) {
    if (SST.isSail(pdi.sunSailType))
      return PD.interned({ pdt: "sunSail", sunSailType: pdi.sunSailType, poleCount: pdi.poleCount || 0, version: pdi.version })
    else if (SST.isRaff(pdi.sunSailType))
      return PD.interned({ pdt: "raff", raffType: pdi.raffType!, sailMount: pdi.sailMount, version: pdi.version })
    else if (SST.isCurtain(pdi.sunSailType))
      return PD.interned({ pdt: "curtain", curtainType: pdi.curtainType!, version: pdi.version })
    else if (SST.isLounge(pdi.sunSailType))
      return PD.interned({ pdt: "lounge", loungeType: pdi.loungeType!, version: pdi.version })
    else if (SST.isModelProduct(pdi.sunSailType))
      return PD.interned({ pdt: "modelProduct", templateId: pdi.templateId!, version: pdi.version })
    else if (SST.isStandardCollection(pdi.sunSailType))
      return PD.interned({ pdt: "standardCollection", version: pdi.version })
    else if (SST.isNet(pdi.sunSailType))
      return PD.interned({ pdt: "net", version: pdi.version, shapeType: pdi.shapeType! })
  }
  throw new Error("productToPD (2d) not detected" + JSON.stringify(pdi))
}